import React, { useState, useEffect } from 'react';
import { Stack, Typography, IconButton, Tooltip } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';

interface GroupedTableFormatterProps {
  row: any;
  groupByColumn: string;
  groupByLabel: string;
  onToggle: (row: any) => void;
  countLabel?: string;
}

const ExpandFormatter: React.FC<{ 
  expanded: boolean; 
  onCellExpand: () => void;
}> = ({ expanded, onCellExpand }) => {
  return (
    <IconButton
      size="small"
      onClick={onCellExpand}
      sx={{
        padding: '2px',
        marginLeft: '4px',
        transition: 'transform 0.2s ease',
        transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <ChevronRight sx={{ fontSize: 20, fontWeight: 700, color: '#003771' }} />
    </IconButton>
  );
};

export const GroupedTableFormatter: React.FC<GroupedTableFormatterProps> = ({ 
  row, 
  groupByColumn, 
  groupByLabel,
  onToggle,
  countLabel,
}) => {
  const [isExpanded, setIsExpanded] = useState(row.isExpanded || false);
  
  useEffect(() => {
    setIsExpanded(row.isExpanded || false);
  }, [row.isExpanded, row[groupByColumn]]);
  
  const isParent = row.isParent === true;
  const hasChildren = isParent && row.children && row.children.length > 0;
  const totalChildrenCount = row.children?.length || 0;
  
  const handleToggle = () => {
    if (!hasChildren) return;
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onToggle(row);
  };
  
  if (!isParent) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 6, textAlign: 'left', width: '100%' }}>
        <Typography sx={{ fontWeight: 500, fontSize: '14px', textAlign: 'left' }}>
          {groupByLabel}
        </Typography>
      </Stack>
    );
  }

  let groupValue = row[groupByColumn] || groupByLabel || 'Unassigned';
  const count = row[`${groupByColumn}Count`] || row?.groupCount || totalChildrenCount;
    const getCountDisplay = () => {
    if (countLabel) {
      return `(${count} ${countLabel})`;
    }
    return `(${count})`;
  };
  if (groupByColumn === 'severity' && groupValue?.toLowerCase() === 'ok') {
    groupValue = 'Info';
  }
  
  return (
    <Stack direction="row" alignItems="center" spacing={1} height="100%" sx={{ width: '100%', justifyContent: 'flex-start' }}>
      {hasChildren && (
        <ExpandFormatter
          expanded={isExpanded}
          onCellExpand={handleToggle}
        />
      )}
      <Tooltip 
        title={groupValue} 
        placement="right-start"
        arrow
        PopperProps={{
          sx: {
            '& .MuiTooltip-tooltip': {
              backgroundColor: '#003771',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 500,
              padding: '6px 12px',
              maxWidth: '400px',
              wordBreak: 'break-word'
            },
            '& .MuiTooltip-arrow': {
              color: '#003771'
            }
          }
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: '#003771',
            paddingLeft: !hasChildren ? '32px' : '0px',
            cursor: hasChildren ? 'pointer' : 'default',
            fontSize: '14px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '750px',
            textAlign: 'left'
          }}
          onClick={handleToggle}
        >
          {groupValue}
        </Typography>
      </Tooltip>
      {hasChildren && (
        <Typography sx={{ fontSize: '12px', color: '#6B7280', flexShrink: 0 }}>
           {getCountDisplay()}
        </Typography>
      )}
    </Stack>
  );
};